import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { getProposalReview, signProposal } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { SignaturePad } from "@/components/SignaturePad";
import { CheckCircle, XCircle, FileText, Loader2, Download } from "lucide-react";
import { COMPANY_INFO } from "@/lib/companyInfo";
import { downloadPdfUrl } from "@/lib/downloadBlob";
import { calcProposalTotals, summarizeProposalDisplay } from "@/components/proposals/proposalUtils";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { addCompanyHeader } from "@/lib/pdfHeader";
import { format as fmtDate } from "date-fns";

const fmt = (v) => Number(v || 0).toLocaleString("en-US", { style: "currency", currency: "USD" });

export default function ProposalReviewPage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [signing, setSigning] = useState(false);
  const [signed, setSigned] = useState(false);
  const [showSignature, setShowSignature] = useState(false);
  const [signedBy, setSignedBy] = useState("");
  const [signResult, setSignResult] = useState(null);

  useEffect(() => {
    document.title = "ServiceOps Proposal";
    (async () => {
      try {
        const res = await getProposalReview(token);
        setData(res.data);
        const name = res.data?.proposal?.name;
        document.title = name ? `ServiceOps Proposal — ${name}` : "ServiceOps Proposal";
        if (res.data.proposal.status === "won") setSigned(true);
      } catch {
        setError("This proposal link is invalid or has expired.");
      } finally {
        setLoading(false);
      }
    })();
  }, [token]);

  // Build executed PDF with signature embedded — base64 dataURI
  const buildExecutedPdf = (proposal, totals, signatureData, signedByName) => {
    const doc = new jsPDF();
    const pageW = doc.internal.pageSize.getWidth();
    const margin = 15;
    let y = addCompanyHeader(doc, 10);

    // Title
    doc.setFontSize(14); doc.setFont(undefined, "bold"); doc.setTextColor(30, 30, 30);
    doc.text("EXECUTED PROPOSAL", margin, y);
    y += 6;
    doc.setFontSize(9); doc.setFont(undefined, "normal"); doc.setTextColor(80, 80, 80);
    doc.text(`${proposal.name || ""}`, margin, y);
    y += 8;

    // PROJECT / CUSTOMER / SITE / ADDRESS / CONTACT / PROPOSAL DATE / PREPARED BY —
    // mirror the layout of the main Download-PDF generator so the emailed
    // link + customer-facing PDF are consistent.
    const prop = proposal;
    const contactName = prop.site_contact_name || prop.contact_name || data.site_contact_name || "";
    const contactEmail = prop.site_contact_email || prop.contact_email || data.site_contact_email || "";
    const contactLine = contactName && contactEmail ? `${contactName} · ${contactEmail}` : (contactName || contactEmail || "—");
    const preparedName = prop.prepared_by || "—";
    const preparedEmail = prop.prepared_by_email || "";
    const preparedLine = preparedName !== "—" && preparedEmail ? `${preparedName} · ${preparedEmail}` : (preparedEmail || preparedName);
    const bidDate = prop.proposal_date ? fmtDate(new Date(prop.proposal_date + "T00:00:00"), "MM/dd/yyyy") : fmtDate(new Date(), "MM/dd/yyyy");
    const rightX = pageW - margin;
    // Row 1 — PROJECT / CUSTOMER
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("PROJECT", margin, y);
    doc.text("CUSTOMER", rightX, y, { align: "right" });
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
    doc.text(prop.name || "—", margin, y + 5);
    doc.text(data.client_name || "—", rightX, y + 5, { align: "right" });
    y += 12;
    // Row 2 — SITE / ADDRESS
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("SITE", margin, y);
    doc.text("ADDRESS", rightX, y, { align: "right" });
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
    doc.text(data.site_name || "Main", margin, y + 5);
    doc.text(data.site_address || proposal.project_address || "—", rightX, y + 5, { align: "right", maxWidth: 90 });
    y += 12;
    // Row 3 — CONTACT / PROPOSAL DATE
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("CONTACT", margin, y);
    doc.text("PROPOSAL DATE", rightX, y, { align: "right" });
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
    doc.text(contactLine, margin, y + 5, { maxWidth: 90 });
    doc.text(bidDate, rightX, y + 5, { align: "right" });
    y += 12;
    // Row 4 — PREPARED BY (left only, includes name + email)
    doc.setFontSize(8); doc.setFont(undefined, "bold"); doc.setTextColor(100, 100, 100);
    doc.text("PREPARED BY", margin, y);
    doc.setFontSize(10); doc.setFont(undefined, "normal"); doc.setTextColor(30, 30, 30);
    doc.text(preparedLine, margin, y + 5);
    doc.setTextColor(0, 0, 0);
    y += 14;

    // Consolidated line items. When proposal.show_breakdown is ON, split
    // Material and Labor into separate COLUMNS (same behavior as WO/CO/Invoice).
    // CC fee is silently baked into material — use `applyCCFeeDistribution` so
    // the executed PDF mirrors the emailed/HTML/PDF customer views.
    const breakout = !!proposal.show_breakdown;
    const feePct = proposal.cc_fee_pct == null ? 0 : proposal.cc_fee_pct;
    // iter244 — add-ons render as aggregate totals lines, not table rows.
    const pdfDisp = summarizeProposalDisplay(proposal.scopes || [], feePct,
                                             proposal.hide_tax ? (totals.taxAmount || 0) : 0);
    const ccScopes = pdfDisp.ccScopes;
    const rows = [];
    ccScopes.forEach(scope => {
      (scope._lines || []).forEach(li => {
        const qty = parseInt(li.qty) || 1;
        const matBilled = li._displayMaterial || 0;
        const labBilled = li._displayLabor || 0;
        const frtBilled = li._displayFreight || 0;
        const total = li._displayTotal || 0;
        if (breakout) {
          rows.push([
            li.part_number || "",
            li.name || li.description || "",
            String(qty),
            fmt(matBilled),
            fmt(frtBilled),
            fmt(labBilled),
            fmt(total),
          ]);
        } else {
          rows.push([
            li.part_number || "",
            li.name || li.description || "",
            String(qty),
            fmt(total / (qty || 1)),
            fmt(total),
          ]);
        }
      });
    });

    autoTable(doc, {
      startY: y,
      head: [breakout
        ? ["Part #", "Description", "Qty", "Material", "Freight", "Labor", "Total"]
        : ["Part #", "Description", "Qty", "Unit", "Total"]],
      body: rows,
      theme: "grid",
      styles: { fontSize: 8 },
      headStyles: { fillColor: [148, 163, 184] },
    });

    y = doc.lastAutoTable.finalY + 8;

    // Totals — Subtotal (items only) → add-ons → Tax → Total (iter244)
    doc.setFontSize(10);
    doc.text(`Subtotal: ${fmt(pdfDisp.itemsSubtotal)}`, pageW - margin, y, { align: "right" });
    y += 5;
    [["Drive Time", pdfDisp.driveTime], ["Equipment", pdfDisp.equipment], ["Travel", pdfDisp.travel], ["Disposal", pdfDisp.disposal]].forEach(([label, amt]) => {
      if ((amt || 0) > 0) {
        doc.text(`${label}: ${fmt(amt)}`, pageW - margin, y, { align: "right" });
        y += 5;
      }
    });
    // iter257 — Discount row in customer PDF download.
    if ((totals.discountAmount || 0) > 0) {
      const dLabel = (totals.discountType || proposal.discount_type) === "pct"
        ? `Discount (${parseFloat(proposal.discount_value) || 0}%)`
        : "Discount";
      doc.text(`${dLabel}: -${fmt(totals.discountAmount)}`, pageW - margin, y, { align: "right" });
      y += 5;
    }
    if (!proposal.hide_tax && (totals.taxAmount || 0) > 0) {
      doc.text(`Tax (${proposal.tax_rate || 0}%): ${fmt(totals.taxAmount)}`, pageW - margin, y, { align: "right" });
      y += 5;
    }
    doc.setFont(undefined, "bold");
    doc.setFontSize(12);
    doc.text(`TOTAL: ${fmt(totals.total)}`, pageW - margin, y, { align: "right" });
    y += 12;

    // Qualifications / Terms
    doc.setFont(undefined, "normal"); doc.setFontSize(9);
    if (proposal.qualifications?.length) {
      doc.setFont(undefined, "bold"); doc.text("Qualifications:", margin, y); y += 5;
      doc.setFont(undefined, "normal");
      proposal.qualifications.forEach(q => {
        const lines = doc.splitTextToSize(`• ${q}`, pageW - 2 * margin);
        doc.text(lines, margin, y);
        y += lines.length * 4;
      });
      y += 2;
    }
    if (proposal.terms?.length) {
      doc.setFont(undefined, "bold"); doc.text("Terms:", margin, y); y += 5;
      doc.setFont(undefined, "normal");
      proposal.terms.forEach(t => {
        const lines = doc.splitTextToSize(`• ${t}`, pageW - 2 * margin);
        doc.text(lines, margin, y);
        y += lines.length * 4;
      });
      y += 2;
    }

    // Ensure signature fits on the page
    if (y > 230) { doc.addPage(); y = 20; }

    // Signature block
    y += 6;
    doc.setFont(undefined, "bold"); doc.setFontSize(10); doc.text("Customer Acceptance & Signature", margin, y);
    y += 4;
    doc.setFont(undefined, "normal"); doc.setFontSize(9);
    try {
      doc.addImage(signatureData, "PNG", margin, y, 70, 25);
    } catch { /* fall-through */ }
    y += 28;
    doc.text(`Signed by: ${signedByName || "—"}`, margin, y); y += 5;
    doc.text(`Date: ${fmtDate(new Date(), "MMM d, yyyy h:mm a")}`, margin, y);

    return doc.output("datauristring");
  };

  const handleSign = async (signatureData) => {
    setSigning(true);
    try {
      // Step 1: send the signature immediately — fast, small payload. Always succeeds first try.
      const res = await signProposal(token, {
        signature_data: signatureData,
        signed_by: signedBy,
      });
      setSignResult(res.data);
      setSigned(true);

      // Step 2: build & upload the executed PDF AFTER user sees success — non-blocking.
      try {
        const totals = calcProposalTotals(data.proposal.scopes || [], data.proposal.tax_rate || 0, data.proposal.cc_fee_pct == null ? 0 : data.proposal.cc_fee_pct, data.proposal.discount_type || "flat", data.proposal.discount_value || 0, data.proposal.tax_override || null);
        const executedPdf = buildExecutedPdf(data.proposal, totals, signatureData, signedBy);
        if (executedPdf) {
          // Fire-and-forget — no spinner, no error toast. Backend already has signature_data.
          signProposal(token, {
            signature_data: signatureData,
            signed_by: signedBy,
            executed_proposal_pdf: executedPdf,
          }).catch(() => { /* PDF upload failed — signature already saved, can be rebuilt PM-side */ });
        }
      } catch (e) {
        console.error("Executed PDF build failed (non-fatal):", e);
      }
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to sign proposal");
    } finally {
      setSigning(false);
    }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center">
        <XCircle className="w-12 h-12 text-red-400 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Link Not Found</h2>
        <p className="text-sm text-slate-500">{error}</p>
      </div>
    </div>
  );

  const { proposal, client_name, site_name, site_address } = data;
  // Site always has a visible name — defaults to "Main" if the proposal
  // doesn't reference a linked site (e.g., inline-created customers).
  const displaySiteName = site_name || "Main";
  // Address falls back to the proposal's own project_address field if the
  // review endpoint didn't resolve a linked site address.
  const displaySiteAddress = site_address || proposal.project_address || "—";
  const scopes = proposal.scopes || [];
  const taxRate = proposal.tax_rate || 0;
  const totals = calcProposalTotals(scopes, taxRate, proposal.cc_fee_pct == null ? 0 : proposal.cc_fee_pct, proposal.discount_type || "flat", proposal.discount_value || 0, proposal.tax_override || null);
  // iter244 — aggregate add-ons for the totals summary display.
  // iter310 — hide-tax proposal: the tax is folded into the Material
  // figures here so the customer never sees a tax line.
  const pageDisp = summarizeProposalDisplay(scopes, proposal.cc_fee_pct == null ? 0 : proposal.cc_fee_pct,
                                            proposal.hide_tax ? (totals.taxAmount || 0) : 0);

  if (signed) return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4">
      <div className="bg-white rounded-xl shadow-lg p-8 max-w-md text-center w-full">
        <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-800 mb-2">Proposal Approved</h2>
        <p className="text-sm text-slate-500 mb-2">
          Thank you for approving this proposal. {COMPANY_INFO.name} has been notified.
        </p>
        {signResult?.wo_number && (
          <p className="text-xs text-slate-400 bg-slate-50 rounded-md px-3 py-2 mt-2 mb-4">
            Work Order <span className="font-mono font-semibold">{signResult.wo_number}</span> has been generated automatically and is ready for scheduling.
          </p>
        )}
        {/* #170 (Group H): only render the download button when the
            backend confirms an executed PDF is on file. Without this,
            a customer clicking the button right after signing (before
            the async build/store finishes) would 404 on the read-only
            GET endpoint. The `signResult.has_executed_pdf` flag is
            returned by /proposal-review/{token}/sign. Falls back to
            the proposal's own `executed_document` presence flag for
            visitors hitting the page after the redirect. */}
        {proposal.id && (signResult?.has_executed_pdf || proposal.has_executed_pdf) && (
          <button
            type="button"
            onClick={() => downloadPdfUrl(`/proposals/${proposal.id}/executed-document?download=1`, {
              suggestedName: `ABS_Proposal_${proposal.proposal_number || proposal.id}_Signed.pdf`,
              label: "signed proposal",
            })}
            className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-slate-800 text-white hover:bg-slate-900 text-sm font-medium shadow"
            data-testid="btn-download-executed-proposal">
            <Download className="w-4 h-4" /> Download Signed Proposal
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="bg-white rounded-xl shadow-lg overflow-hidden">
          {/* Header */}
          <div className="bg-slate-800 text-white p-6 flex items-center gap-4">
            <img src="/abs-logo.png" alt={COMPANY_INFO.name} className="abs-logo h-12 w-auto bg-white rounded p-1" />
            <div className="flex-1">
              <div className="text-xs font-medium opacity-70 mb-1">{COMPANY_INFO.name}</div>
              <h1 className="text-xl font-bold">Proposal for Review & Approval</h1>
              <div className="text-sm opacity-80 mt-1">{proposal.name}</div>
            </div>
          </div>

          <div className="p-6 space-y-6">
            {/* Details — mirrors the PDF layout: PROJECT/CUSTOMER, SITE/ADDRESS,
                CONTACT/PROPOSAL DATE, PREPARED BY. */}
            {(() => {
              const contactName = proposal.site_contact_name || proposal.contact_name || data.site_contact_name || "";
              const contactEmail = proposal.site_contact_email || proposal.contact_email || data.site_contact_email || "";
              const contactLine = contactName && contactEmail ? `${contactName} · ${contactEmail}` : (contactName || contactEmail || "—");
              const preparedName = proposal.prepared_by || "";
              const preparedEmail = proposal.prepared_by_email || "";
              const preparedLine = preparedName && preparedEmail ? `${preparedName} · ${preparedEmail}` : (preparedName || preparedEmail || "—");
              return (
                <div className="grid grid-cols-2 gap-x-6 gap-y-3 text-sm">
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Project</div>
                    <div className="font-medium text-slate-800">{proposal.name || "—"}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Customer</div>
                    <div className="font-medium text-slate-800">{client_name || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Site</div>
                    <div className="font-medium text-slate-800">{displaySiteName}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Address</div>
                    <div className="font-medium text-slate-800">{displaySiteAddress}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Contact</div>
                    <div className="font-medium text-slate-800">{contactLine}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Proposal Date</div>
                    <div className="font-medium text-slate-800">{proposal.proposal_date || "—"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">Prepared By</div>
                    <div className="font-medium text-slate-800">{preparedLine}</div>
                  </div>
                </div>
              );
            })()}

            {/* Scopes & Line Items — customer-facing consolidated view.
                When proposal.show_breakdown is ON, render a 6-column table
                (Part # | Description | Qty | Material | Labor | Total) that
                mirrors the generated PDF. CC fee is silently distributed into
                material via applyCCFeeDistribution. */}
            {(() => {
              const breakout = !!proposal.show_breakdown;
              const ccScopes = pageDisp.ccScopes;
              return ccScopes.map((scope, si) => {
                const lineRows = (scope._lines || []).map((li, i) => ({
                  key: li.id || i,
                  partNumber: li.part_number || "",
                  name: li.name || li.description || "Item",
                  description: li.description && li.name && li.description !== li.name ? li.description : "",
                  qty: parseInt(li.qty) || 1,
                  material: li._displayMaterial || 0,
                  freight: li._displayFreight || 0,
                  labor: li._displayLabor || 0,
                  total: li._displayTotal || 0,
                }));
                return (
                  // eslint-disable-next-line react/no-array-index-key -- proposal review scope order is the customer-facing snapshot
                  <div key={`sc-${si}-${scope.name || ''}`} className="mb-5" data-testid={`scope-card-${si}`}>
                    {/* Scope header — mirrors the PDF's uppercase bold scope
                        title (no tinted box, no $ total next to the name). */}
                    <h3 className="text-sm font-bold text-slate-800 mb-1 uppercase tracking-wide">{scope.name || `Scope ${si + 1}`}</h3>
                    {breakout ? (
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm" data-testid={`scope-breakout-table-${si}`}>
                          <thead>
                            <tr className="bg-slate-700 text-white">
                              <th className="text-left px-3 py-1.5 font-semibold">Part #</th>
                              <th className="text-left px-3 py-1.5 font-semibold">Description</th>
                              <th className="text-center px-3 py-1.5 font-semibold">Qty</th>
                              <th className="text-right px-3 py-1.5 font-semibold">Material</th>
                              <th className="text-right px-3 py-1.5 font-semibold" data-testid={`review-col-freight-${si}`}>Freight</th>
                              <th className="text-right px-3 py-1.5 font-semibold">Labor</th>
                              <th className="text-right px-3 py-1.5 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y">
                            {lineRows.map(r => (
                              <tr key={r.key}>
                                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap font-mono">{r.partNumber || "—"}</td>
                                <td className="px-3 py-1.5 text-slate-700">
                                  <div className="font-medium">{r.name}</div>
                                  {r.description && <div className="text-[11px] text-slate-400">{r.description}</div>}
                                </td>
                                <td className="px-3 py-1.5 text-center text-slate-600 tabular-nums">{r.qty}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmt(r.material)}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmt(r.freight)}</td>
                                <td className="px-3 py-1.5 text-right tabular-nums text-slate-700">{fmt(r.labor)}</td>
                                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-800">{fmt(r.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        {/* Non-breakout layout mirrors the "View" PDF:
                            4-column table (Part # / Name / Qty / Total),
                            striped rows. No scope-subtotal footer row —
                            the grand Total block below is the source of
                            truth. */}
                        <table className="w-full text-sm" data-testid={`scope-summary-table-${si}`}>
                          <thead>
                            <tr className="bg-slate-700 text-white">
                              <th className="text-left px-3 py-1.5 font-semibold">Part #</th>
                              <th className="text-left px-3 py-1.5 font-semibold">Name</th>
                              <th className="text-center px-3 py-1.5 font-semibold">Qty</th>
                              <th className="text-right px-3 py-1.5 font-semibold">Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineRows.map((r, rIdx) => (
                              <tr key={r.key} className={rIdx % 2 === 0 ? "bg-white" : "bg-slate-50/60"}>
                                <td className="px-3 py-1.5 text-slate-500 whitespace-nowrap font-mono">{r.partNumber || "—"}</td>
                                <td className="px-3 py-1.5 text-slate-700">
                                  <div className="font-medium">{r.name}</div>
                                  {r.description && <div className="text-[11px] text-slate-400">{r.description}</div>}
                                </td>
                                <td className="px-3 py-1.5 text-center text-slate-600 tabular-nums">{r.qty}</td>
                                <td className="px-3 py-1.5 text-right font-semibold tabular-nums text-slate-800">{fmt(r.total)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                );
              });
            })()}

            {/* Totals — mirrors the "View" PDF: right-aligned stack with
                Subtotal → Tax → a thin divider → bold "Total" in black. */}
            <div className="flex justify-end">
              <div className="text-right space-y-1 min-w-[280px]">
                <div className="text-sm text-slate-500 flex justify-between gap-8"><span>Subtotal</span><span className="tabular-nums" data-testid="review-items-subtotal">{fmt(pageDisp.itemsSubtotal)}</span></div>
                {(pageDisp.driveTime || 0) > 0 && (
                  <div className="text-sm text-slate-500 flex justify-between gap-8" data-testid="review-drivetime"><span>Drive Time</span><span className="tabular-nums">{fmt(pageDisp.driveTime)}</span></div>
                )}
                {(pageDisp.equipment || 0) > 0 && (
                  <div className="text-sm text-slate-500 flex justify-between gap-8" data-testid="review-equipment"><span>Equipment</span><span className="tabular-nums">{fmt(pageDisp.equipment)}</span></div>
                )}
                {(pageDisp.travel || 0) > 0 && (
                  <div className="text-sm text-slate-500 flex justify-between gap-8" data-testid="review-travel"><span>Travel</span><span className="tabular-nums">{fmt(pageDisp.travel)}</span></div>
                )}
                {(pageDisp.disposal || 0) > 0 && (
                  <div className="text-sm text-slate-500 flex justify-between gap-8" data-testid="review-disposal"><span>Disposal</span><span className="tabular-nums">{fmt(pageDisp.disposal)}</span></div>
                )}
                {(totals.discountAmount || 0) > 0 && (
                  <div className="text-sm text-emerald-700 flex justify-between gap-8" data-testid="review-discount">
                    <span>
                      {(totals.discountType || proposal.discount_type) === "pct"
                        ? `Discount (${parseFloat(proposal.discount_value) || 0}%)`
                        : "Discount"}
                    </span>
                    <span className="tabular-nums">-{fmt(totals.discountAmount)}</span>
                  </div>
                )}
                {proposal.hide_tax ? null : (totals.taxAmount || 0) > 0 ? (
                  <div className="text-sm text-slate-500 flex justify-between gap-8"><span>Tax ({taxRate}%)</span><span className="tabular-nums">{fmt(totals.taxAmount)}</span></div>
                ) : (
                  <div className="text-sm text-slate-500 flex justify-between gap-8"><span>Tax ({taxRate || 0}%)</span><span className="tabular-nums">{fmt(0)}</span></div>
                )}
                <div className="pt-2 border-t border-slate-300 flex justify-between items-baseline gap-8">
                  <span className="text-base font-bold text-slate-800">Total</span>
                  <span className="text-2xl font-bold text-slate-900 tabular-nums">{fmt(totals.total)}</span>
                </div>
              </div>
            </div>

            {/* Qualifications */}
            {proposal.qualifications?.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Qualifications</h3>
                <ul className="text-xs text-slate-600 space-y-0.5 list-disc ml-4">
                  {proposal.qualifications.map((q) => <li key={`pq-${q}`}>{q}</li>)}
                </ul>
              </div>
            )}
            {/* Terms & Exclusions */}
            {proposal.terms?.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Terms & Conditions</h3>
                <ul className="text-xs text-slate-600 space-y-0.5 list-disc ml-4">
                  {proposal.terms.map((t) => <li key={`pt-${t}`}>{t}</li>)}
                </ul>
              </div>
            )}
            {proposal.exclusions?.length > 0 && (
              <div>
                <h3 className="text-xs font-semibold text-slate-400 uppercase mb-1">Exclusions</h3>
                <ul className="text-xs text-slate-600 space-y-0.5 list-disc ml-4">
                  {proposal.exclusions.map((e) => <li key={`pe-${e}`}>{e}</li>)}
                </ul>
              </div>
            )}

            {/* Sign Section */}
            <div className="border-t pt-6">
              {!showSignature ? (
                <div className="text-center">
                  <p className="text-sm text-slate-500 mb-4">By signing below, you approve this proposal and authorize {COMPANY_INFO.name} to proceed. Your account manager will be notified right away.</p>
                  <Button size="lg" className="bg-blue-600 hover:bg-blue-700 text-white px-8" onClick={() => setShowSignature(true)} data-testid="btn-approve-proposal">
                    <FileText className="w-4 h-4 mr-2" /> Review & Sign
                  </Button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700">Your Name</label>
                    <Input value={signedBy} onChange={(e) => setSignedBy(e.target.value)} placeholder="Full name" className="mt-1" data-testid="input-signed-by" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-2 block">Signature</label>
                    <SignaturePad
                      onSave={(sigData) => handleSign(sigData)}
                      height={150}
                    />
                  </div>
                  {signing && <div className="text-center text-sm text-blue-500"><Loader2 className="w-4 h-4 inline animate-spin mr-1" />Submitting...</div>}
                  <Button variant="outline" size="sm" onClick={() => setShowSignature(false)}>Cancel</Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
